import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2,
  TrendingDown, TrendingUp, Trash2, RefreshCw, Sparkles, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/app/DatePicker";
import { InfoTip } from "@/components/app/InfoTip";
import { useAuth } from "@/contexts/AuthContext";

const FIELD_LABELS = {
  term: "Search term *",
  campaign: "Campaign name",
  ad_group: "Ad group",
  match_type: "Match type",
  impressions: "Impressions",
  clicks: "Clicks",
  spend: "Spend",
  sales: "Sales",
  orders: "Orders",
  acos: "ACoS",
  ctr: "CTR",
  cpc: "CPC",
  reporting_date: "Reporting date",
};

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function AdsPerformance() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const { user } = useAuth();

  const [marketplace, setMarketplace] = useState("Amazon");
  const [country, setCountry] = useState("India");
  const [weekStart, setWeekStart] = useState("");
  const [weekEnd, setWeekEnd] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewServerWait, setPreviewServerWait] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processProgress, setProcessProgress] = useState(0);
  const [processServerWait, setProcessServerWait] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const [uploads, setUploads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isAdmin = user?.role === "admin";
  const canUpload = ["admin", "brand_manager"].includes(user?.role);

  useEffect(() => {
    if (selectedBrand?.marketplace) setMarketplace(selectedBrand.marketplace);
    if (selectedBrand?.country) setCountry(selectedBrand.country);
  }, [selectedBrand]);

  const reload = async () => {
    if (!selectedBrandId) return;
    setLoadingHistory(true);
    try {
      const [u, s] = await Promise.all([
        api.get(`/ads/uploads?brand_id=${selectedBrandId}`),
        api.get(`/ads/summary?brand_id=${selectedBrandId}`),
      ]);
      setUploads(u.data?.uploads || []);
      setSummary(s.data || null);
    } catch (e) {
      toast.error(extractError(e, "Could not load ads history"));
    } finally { setLoadingHistory(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  const onPickFile = async (f) => {
    setError(""); setFile(f); setPreview(null); setMapping({});
    if (!f) return;
    setPreviewLoading(true); setPreviewProgress(0); setPreviewServerWait(false);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ads/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          setPreviewProgress(pct);
          if (pct >= 100) setPreviewServerWait(true);
        },
      });
      setPreview(data);
      setMapping(data.auto_mapping || {});
      if (data.detected_week_start && !weekStart) setWeekStart(data.detected_week_start);
      if (data.detected_week_end && !weekEnd) setWeekEnd(data.detected_week_end);
      const mapped = Object.keys(data.auto_mapping || {}).length;
      toast.success(`Detected ${mapped} column${mapped === 1 ? "" : "s"} · ${data.total_rows} rows`);
    } catch (e) {
      setError(extractError(e, "Could not parse file"));
    } finally {
      setPreviewLoading(false); setPreviewServerWait(false);
    }
  };

  const submit = async () => {
    if (!mapping.term) return toast.error("Search-term column not mapped.");
    if (!weekStart || !weekEnd) return toast.error("Pick the report's week range.");
    setSubmitting(true); setError("");
    setProcessProgress(0); setProcessServerWait(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand_id", selectedBrandId);
      fd.append("marketplace", marketplace);
      fd.append("country", country);
      fd.append("week_start_date", weekStart);
      fd.append("week_end_date", weekEnd);
      fd.append("mapping_json", JSON.stringify(mapping));
      const { data } = await api.post("/ads/process", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
          setProcessProgress(pct);
          if (pct >= 100) setProcessServerWait(true);
        },
      });
      setResult(data);
      toast.success(`Imported ${data.unique_terms} terms — closed-loop signals will appear on next reco regen`);
      reload();
    } catch (e) {
      setError(extractError(e, "Upload failed"));
    } finally { setSubmitting(false); setProcessServerWait(false); }
  };

  const deleteUpload = async (u) => {
    const summary = [
      `Week: ${u.week_start_date} → ${u.week_end_date}`,
      `File: ${u.file_name}`,
      `Rows: ${u.unique_terms || 0}`,
      `Spend: ₹${Number(u.totals?.spend || 0).toLocaleString("en-IN")}`,
    ].join("\n");
    if (!window.confirm(
      `Roll back this ad upload?\n\n${summary}\n\nThis will permanently delete the upload row AND every per-keyword ad_performance entry tied to it. The closed-loop signals (boost / auto-negative) generated from this week will disappear from the Bidding page. Real Amazon ad data is unaffected.\n\nContinue?`
    )) return;
    try {
      const r = await api.delete(`/ads/uploads/${u.id}`);
      toast.success(`Rolled back · ${r.data.deleted_rows ?? r.data.deleted_terms ?? "0"} rows removed`);
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const headerOptions = useMemo(() => preview?.headers || [], [preview]);

  if (!selectedBrandId) return <div className="text-sm text-slate-500">Select a brand from the top bar.</div>;

  const t = summary?.latest?.totals || {};
  const acosColor = (t.acos == null) ? "text-slate-700" : (t.acos < 25 ? "text-emerald-700" : (t.acos < 40 ? "text-amber-700" : "text-rose-700"));

  return (
    <div className="space-y-8" data-testid="ads-performance-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution · closed-loop</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 inline-flex items-center gap-2">
            Ad performance
            <InfoTip term="AD_PERF_UPLOAD" />
          </h1>
          <p className="mt-1 text-sm text-slate-500 inline-flex items-baseline gap-1.5 flex-wrap">
            Upload your weekly Amazon Sponsored Products / Search-Term Report.
            We auto-detect columns and feed every metric back into next week's
            bid recommendations <InfoTip term="CLOSED_LOOP" className="-mb-0.5" /> as either
            <span className="font-semibold text-emerald-700 inline-flex items-center gap-0.5">boost <InfoTip term="BID_BOOST" /></span> or
            <span className="font-semibold text-rose-700 inline-flex items-center gap-0.5">auto-negative <InfoTip term="AUTO_NEGATIVE" /></span> signals.
          </p>
        </div>
        <Button data-testid="ads-reload" variant="outline" onClick={reload} className="gap-1.5">
          <RefreshCw className={`h-4 w-4 ${loadingHistory ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Latest week totals */}
      {summary?.latest && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="ads-latest-totals">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Latest week</div>
            <div className="mt-2 text-base font-semibold text-slate-900 inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {summary.latest.week_start_date} → {summary.latest.week_end_date}
            </div>
            <div className="mt-1 text-xs text-slate-500">{summary.latest.unique_terms || 0} unique terms</div>
          </div>
          <KpiCell label="Spend" value={fmtINR(t.spend)} testid="ads-kpi-spend" />
          <KpiCell label="Sales" value={fmtINR(t.sales)} testid="ads-kpi-sales" />
          <KpiCell label="ACoS" tip="ACOS" value={t.acos != null ? `${t.acos}%` : "—"} className={acosColor} testid="ads-kpi-acos" />
          <KpiCell label="Orders" value={(t.orders || 0).toLocaleString()} testid="ads-kpi-orders" />
        </div>
      )}

      {/* Uploader */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg flex items-center gap-2">
            <UploadCloud className="h-4 w-4" /> Upload weekly ads report
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          {!canUpload && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 inline-flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> Viewers / marketing executives cannot upload reports.
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">CSV / XLSX file</Label>
              <Input
                id="ads-file-input"
                data-testid="ads-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                disabled={!canUpload}
                onChange={(e) => onPickFile(e.target.files?.[0] || null)}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Accepts Amazon SP "Search Term Report" CSV/XLSX. Auto-detects columns by name.
              </p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Week start</Label>
                  <DatePicker value={weekStart} onChange={setWeekStart} testid="ads-week-start" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Week end</Label>
                  <DatePicker value={weekEnd} onChange={setWeekEnd} testid="ads-week-end" />
                </div>
              </div>
            </div>
          </div>

          {(previewLoading) && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ads-preview-progress">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-3.5 w-3.5 text-slate-700" />
                <div className="text-xs font-semibold text-slate-900 truncate flex-1">{file?.name}</div>
                <div className="text-[10px] text-slate-500">
                  {previewServerWait ? "Parsing report…" : `${previewProgress}%`}
                </div>
              </div>
              <Progress value={previewProgress} className="h-2" />
            </div>
          )}

          {preview && (
            <div className="space-y-4">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
                Detected <span className="font-semibold text-slate-900">{preview.headers.length}</span> columns ·
                <span className="font-semibold text-slate-900"> {preview.total_rows.toLocaleString()}</span> rows ·
                {preview.detected_week_start && (
                  <span> auto week <span className="font-semibold text-slate-900">{preview.detected_week_start} → {preview.detected_week_end}</span></span>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Column mapping</div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(FIELD_LABELS).map(([field, label]) => (
                    <div key={field} className="flex items-center gap-2">
                      <Label className="w-32 text-xs text-slate-600">{label}</Label>
                      <Select
                        value={mapping[field] || "__none__"}
                        onValueChange={(v) =>
                          setMapping((m) => ({ ...m, [field]: v === "__none__" ? undefined : v }))
                        }
                      >
                        <SelectTrigger data-testid={`ads-map-${field}`} className="flex-1 text-xs h-8">
                          <SelectValue placeholder="(unmapped)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">(unmapped)</SelectItem>
                          {headerOptions.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {!mapping.term && preview.headers?.length > 0 && (() => {
                const headerStr = preview.headers.join(" ").toLowerCase();
                const looksLikeSD = /\b(cpm|imdb|viewable|audience|sponsored display|targeting_expression)\b/.test(headerStr);
                return (
                  <div data-testid="ads-wrong-report-warning" className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                    <div className="font-semibold inline-flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      {looksLikeSD
                        ? "This looks like a Sponsored Display / DSP report, not a Sponsored Products Search Term Report"
                        : "This looks like a Campaign / Ad-Group report, not a Search Term Report"}
                    </div>
                    {looksLikeSD ? (
                      <p className="mt-1.5 text-xs leading-relaxed">
                        We detected CPM / audience-targeting columns (e.g. <span className="font-mono">Media spend CPM</span>, <span className="font-mono">TARGETING_EXPRESSION</span>, <span className="font-mono">Main IMDb ad impressions</span>).
                        Sponsored Display targets <em>audiences and products</em>, not customer search terms — that's why the <span className="font-mono">Search term</span> column is blank on every row.
                        Closed-loop learning can only act on per-keyword data from Sponsored Products.
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs leading-relaxed">
                        Your CSV doesn't contain a <span className="font-mono font-semibold">Customer Search Term</span> column —
                        we need it because closed-loop learning works <em>per individual search term</em> (it decides
                        auto-negatives and bid boosts on each keyword, not on the ad-group total).
                      </p>
                    )}
                    <p className="mt-2 text-xs leading-relaxed">
                      <span className="font-semibold">Fix:</span> In Amazon Ads → <span className="font-mono">Create report → Ad product = Sponsored Products only</span> (uncheck Sponsored Display + Sponsored Brands).
                      Add <span className="font-mono">Customer search term</span> as a dimension. The export must have a populated <span className="font-mono">Search term</span> column.
                    </p>
                    <p className="mt-2 text-[11px] text-amber-800">
                      Detected columns: <span className="font-mono">{preview.headers.join(", ")}</span>
                    </p>
                  </div>
                );
              })()}

              {error && (
                <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800 inline-flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5" /> {error}
                </div>
              )}

              {(submitting) && (
                <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ads-process-progress">
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-slate-700" />
                    <div className="text-xs font-semibold text-slate-900 truncate flex-1">{file?.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {processServerWait ? "Indexing rows…" : `${processProgress}%`}
                    </div>
                  </div>
                  <Progress value={processProgress} className="h-2" />
                  <div className="mt-2 text-[11px] flex items-center gap-1.5 text-slate-600">
                    <Loader2 className="h-3 w-3 animate-spin text-amber-600" />
                    <span className="text-amber-700">{processServerWait ? "Server is computing closed-loop signals" : "Uploading file"}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  data-testid="ads-submit"
                  disabled={submitting || !canUpload || !mapping.term || !weekStart || !weekEnd}
                  onClick={submit}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  title={!mapping.term ? "Map the Customer Search Term column first — see the warning above." : undefined}
                >
                  {submitting ? "Processing…" : "Process & feed back"}
                </Button>
              </div>
            </div>
          )}

          {result && (
            result.unique_terms === 0 ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm" data-testid="ads-result-empty">
                <div className="font-semibold text-amber-900 inline-flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Upload registered, but 0 unique search terms found
                </div>
                <div className="mt-1.5 text-xs text-amber-900 space-y-0.5">
                  <div>Every data row had a blank <span className="font-mono">Search term</span> value — usually because the file is a Sponsored Display / DSP export (audience-based) instead of a Sponsored Products Search Term Report.</div>
                  <div className="mt-1.5">No closed-loop signals were created. Use the <span className="font-semibold">Roll back</span> button on this upload's history row to remove the empty entry, then re-export with <span className="font-mono">Ad product = Sponsored Products only</span>.</div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm" data-testid="ads-result">
                <div className="font-semibold text-emerald-900 inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Imported {result.unique_terms} unique terms
                </div>
                <div className="mt-1 text-xs text-emerald-800 space-y-0.5">
                  <div>Spend: {fmtINR(result.totals?.spend)} · Sales: {fmtINR(result.totals?.sales)} · ACoS: {result.totals?.acos ?? "—"}%</div>
                  <div className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> Re-run "Generate recommendations" on the Bidding page to see the boost / auto-negative signals applied.</div>
                </div>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Upload history */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg">Upload history</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {!uploads.length ? (
            <div className="text-sm text-slate-500 py-8 text-center">No ad-performance uploads yet.</div>
          ) : (
            <Table data-testid="ads-uploads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Week</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">ACoS</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-mono text-xs">{u.week_start_date} → {u.week_end_date}</TableCell>
                    <TableCell className="text-xs truncate max-w-[200px]">{u.file_name}</TableCell>
                    <TableCell className="text-right text-xs">{u.unique_terms || 0}</TableCell>
                    <TableCell className="text-right text-xs">{fmtINR(u.totals?.spend)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtINR(u.totals?.sales)}</TableCell>
                    <TableCell className="text-right text-xs">{u.totals?.acos != null ? `${u.totals.acos}%` : "—"}</TableCell>
                    <TableCell className="text-right text-xs">{u.totals?.orders || 0}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button
                          data-testid={`ads-delete-${u.id}`}
                          size="sm" variant="ghost"
                          onClick={() => deleteUpload(u)}
                          className="inline-flex items-center gap-1.5 text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-8 px-2"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="text-xs font-medium">Roll back</span>
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Wasted-spend leaderboard */}
      <WastedSpendCard brandId={selectedBrandId} />
    </div>
  );
}

function KpiCell({ label, value, className = "text-slate-900", testid, tip }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
        {label}{tip ? <InfoTip term={tip} /> : null}
      </div>
      <div className={`mt-2 text-2xl font-bold tracking-tight ${className}`}>{value}</div>
    </div>
  );
}

function WastedSpendCard({ brandId }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(4);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    api.get(`/ads/wasted-spend?brand_id=${brandId}&weeks=${weeks}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [brandId, weeks]);

  if (!data) {
    return (
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-600" /> Wasted spend leaderboard</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="text-sm text-slate-500 py-6 text-center">{loading ? "Loading…" : "No data."}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ads-wasted-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-rose-600" /> Wasted spend leaderboard
            <InfoTip term="WASTED_SPEND" />
          </CardTitle>
          <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v, 10))}>
            <SelectTrigger className="w-[140px] h-8" data-testid="ads-wasted-weeks"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 4, 8, 12].map((n) => <SelectItem key={n} value={String(n)}>Last {n} week{n === 1 ? "" : "s"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <KpiCell label="Wasted ₹" tip="WASTED_SPEND" value={fmtINR(data.wasted_spend)} className="text-rose-700" testid="kpi-wasted-spend" />
          <KpiCell label="Wasted clicks" value={data.wasted_clicks?.toLocaleString() || 0} className="text-rose-700" testid="kpi-wasted-clicks" />
          <KpiCell label="Total spend" value={fmtINR(data.total_spend)} testid="kpi-total-spend" />
          <KpiCell label="Overall ACoS" tip="ACOS" value={data.overall_acos != null ? `${data.overall_acos}%` : "—"} testid="kpi-overall-acos" />
        </div>
        {data.top_terms?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Term</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Impressions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_terms.map((r) => (
                <TableRow key={r.normalized_term}>
                  <TableCell className="font-mono text-xs">{r.term}</TableCell>
                  <TableCell className="text-right text-xs text-rose-700 font-semibold">{fmtINR(r.spend)}</TableCell>
                  <TableCell className="text-right text-xs">{r.clicks}</TableCell>
                  <TableCell className="text-right text-xs">{r.impressions?.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-sm text-slate-500 py-6 text-center inline-flex items-center gap-1.5 justify-center w-full">
            <TrendingUp className="h-4 w-4 text-emerald-600" /> No wasted spend in the selected window — every term converted at least once.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
